// ============================================================
// Protocol Track Engine
// ------------------------------------------------------------
// One engine, multiple ways of populating its food list.

// ---- PHYSICIAN GATE --------------------------------------------------------
// The common-trigger track directs a broad elimination WITHOUT lab justification,
// which is a bigger clinical step than the flagged track. It must not go live to
// real users until the medical advisor has signed off on the tier food lists,
// the GI/FODMAP logic, and the timings. Flip to true only after sign-off.
// The flagged track is unaffected by this and stays fully live.
export const COMMON_TRACK_ENABLED = true

// ---------------------------------------------------------------------------
// AWAY GATE THRESHOLDS — consecutive unlogged days mid-elimination.
// NOT a clinical standard until reviewed by the medical advisor (see master doc).
// 1..SOFT_MIN-1  : streak reset only, life happens.
// SOFT_MIN..HARD_MIN-1 : attestation gate on return — one question, user chooses
//                        resume (gap marked ATTESTED, not observed) or restart.
// HARD_MIN+      : restart required. With this much silence there is no evidence
//                  elimination happened; a verdict built on it would cite fiction.
// ---------------------------------------------------------------------------
export const AWAY_GATE = { SOFT_MIN: 4, HARD_MIN: 14 }

// The rest of the app asks getProtocolFoods() for "what is this
// user eliminating and reintroducing" instead of reading the
// lab results directly. That single seam lets the assigned track
// drive the protocol.
//
// Tracks (assigned by admin):
//   flagged  -> test the lab-flagged foods (by tier), as before
//   common   -> clean panel + symptoms; test common triggers.
//               The USER picks Tier 1 (essentials) or Tier 2 (full panel).
//   declined -> no protocol; routed to self-tracking instead.
//
// CLINICAL NOTE: the tier food lists below are research-based
// defaults (6-Food Elimination Diet + top FODMAP triggers) and
// are NOT a clinical standard until reviewed by the medical advisor.
// FODMAP foods are flagged so they can be emphasised for GI symptoms.
// ============================================================

// ---- Common-track tier food sets -------------------------------------------
// Each food carries a default `level` so the existing reintro engine (which
// expects High/Moderate/Low) has something to work with. Common-track foods
// have no lab severity, so they all sit in one tier and default to 'Moderate'.

export const COMMON_TIER_1 = [
  { name: 'Dairy', level: 'Common', fodmap: false },
  { name: 'Gluten / wheat', level: 'Common', fodmap: false },
]

export const COMMON_TIER_2 = [
  { name: 'Dairy', level: 'Common', fodmap: false },
  { name: 'Gluten / wheat', level: 'Common', fodmap: false },
  { name: 'Eggs', level: 'Common', fodmap: false },
  { name: 'Soy', level: 'Common', fodmap: false },
  { name: 'Tree nuts', level: 'Common', fodmap: false },
  { name: 'Corn', level: 'Common', fodmap: false },
  { name: 'Onion / garlic', level: 'Common', fodmap: true },
  { name: 'Legumes', level: 'Common', fodmap: true },
]

// User-facing labels for the two tiers (plain language, the user sees these).

// =============================================================================
// TESTING ORDER DOCTRINE (Alex-approved; FLAGGED FOR PHYSICIAN REVIEW)
// The deterministic process that decides which food gets tested next:
//   1. EATING FREQUENCY first (daily -> 3-5x -> 1-2x -> rarely -> almost-never).
//      The foods someone eats most are the biggest daily-life unlocks and the
//      likeliest contributors to their baseline symptoms. 'never' foods are
//      excluded from the protocol entirely upstream.
//   2. SENSITIVITY LEVEL as tiebreak (High -> Moderate -> Low -> Common).
//      Among equally-eaten foods, resolve the strongest lab suspects first.
//   3. NAME alphabetical as final tiebreak, so the order is fully deterministic
//      and two sessions never disagree.
// Users may override by picking any food manually; this order is the default
// queue shown everywhere (gallery frames, reintro picker, briefings).
// =============================================================================
const ORDER_FREQ_RANK = { 'daily': 1, '3-5x': 2, '1-2x': 3, 'rarely': 4, 'almost-never': 5, 'never': 6 }
const ORDER_LEVEL_RANK = { 'High': 1, 'Moderate': 2, 'Low': 3, 'Common': 4 }
export function getTestingOrder(foods, foodFrequency) {
  return [...(foods || [])].sort((a, b) => {
    const fa = ORDER_FREQ_RANK[foodFrequency?.[a.name]] || 9
    const fb = ORDER_FREQ_RANK[foodFrequency?.[b.name]] || 9
    if (fa !== fb) return fa - fb
    const la = ORDER_LEVEL_RANK[a.level] || 9
    const lb = ORDER_LEVEL_RANK[b.level] || 9
    if (la !== lb) return la - lb
    return (a.name || '').localeCompare(b.name || '')
  })
}

export const TIER_META = {
  1: { key: 1, label: 'Test 2 Foods', sub: 'The essentials', foods: COMMON_TIER_1 },
  2: { key: 2, label: 'Test 8 Foods', sub: 'The full panel', foods: COMMON_TIER_2 },
}

// ---- Per-track timeline parameters -----------------------------------------
// Currently flagged and common both run the full programme. Kept as a per-track
// table so durations can be tuned later (e.g. by the advisor) without touching
// engine logic. The 3-day exposure is constant everywhere and lives in the
// reintro engine, not here.
export const TRACK_TIMELINE = {
  flagged:  { eliminationDays: 56, cycleDays: 14 },
  common:   { eliminationDays: 56, cycleDays: 14 },
  declined: { eliminationDays: 0,  cycleDays: 0 },
}

// ---- The resolver: the single source of "what foods is this protocol" ------
// profile: the user's profile row (has protocol_track, protocol_tier)
// labResult: their lab result row (has .foods for the flagged track)
// Returns { track, foods: [{name, level, fodmap?}], timeline }
export function getProtocolFoods(profile, labResult) {
  const track = profile?.protocol_track || (labResult?.foods?.length ? 'flagged' : null)

  if (track === 'declined') {
    return { track: 'declined', foods: [], timeline: TRACK_TIMELINE.declined }
  }

  if (track === 'common') {
    const tier = Number(profile?.protocol_tier) === 2 ? 2 : 1
    // Lab-flagged foods JOIN the elimination and the testing schedule (Alex's #17 ruling):
    // a real flagged sensitivity left on the plate would contaminate the clean baseline
    // every common-trigger verdict is measured against. On a name collision the lab
    // entry wins, because a lab level is an earned finding and 'Common' is not.
    const labFlagged = (labResult?.foods || []).filter(f => f.level && f.level !== 'No sensitivity')
    const tierFoods = TIER_META[tier].foods.filter(tf => !labFlagged.some(lf => lf.name.toLowerCase() === tf.name.toLowerCase()))
    return {
      track: 'common',
      tier,
      foods: [...tierFoods, ...labFlagged],
      timeline: TRACK_TIMELINE.common,
    }
  }

  // Default / flagged: use the lab-flagged foods exactly as before.
  return {
    track: 'flagged',
    foods: (labResult?.foods || []),
    timeline: TRACK_TIMELINE.flagged,
  }
}

// Convenience: just the food names the user is eliminating/testing.
export function getProtocolFoodNames(profile, labResult) {
  return getProtocolFoods(profile, labResult).foods.map(f => f.name)
}

// Whether the common track should emphasise FODMAP foods, based on whether the
// user's symptoms are GI-related. Used to guide (not force) the admin + framing.
export function symptomsAreGI(profile) {
  const s = profile?.symptoms
  const arr = Array.isArray(s) ? s : (typeof s === 'string' ? [s] : [])
  const joined = arr.join(' ').toLowerCase()
  return /digest|bloat|gas|stomach|gut|ibs|reflux|constip|diarr/.test(joined)
}
